import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Plus, Send, FileText, GraduationCap, CalendarClock, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import TrainingRecordDialog from "@/components/training/TrainingRecordDialog";
import FileAttachments from "@/components/FileAttachments";

function StatusChip({ status }) {
  const map = {
    compliant:     { label: "Compliant", bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-200" },
    expiring:      { label: "Expiring",  bg: "bg-amber-100", text: "text-amber-900", border: "border-amber-300" },
    expired:       { label: "Expired",   bg: "bg-red-100", text: "text-red-800", border: "border-red-200" },
    not_completed: { label: "Not done",  bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
    in_progress:   { label: "In progress", bg: "bg-sky-100", text: "text-sky-800", border: "border-sky-200" },
  };
  const s = map[status] || map.not_completed;
  return <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>{s.label}</span>;
}

export default function StaffProfileDrawer({ staffId, onClose, canManage, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [certRow, setCertRow] = useState(null);

  const load = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const r = await api.get(`/training/staff/${staffId}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);

  if (!staffId) return null;

  const sendReminder = async () => {
    const gaps = [...(data?.missing || []), ...(data?.expired || []), ...(data?.expiring || [])];
    const ids = gaps.map((g) => g.course_id).filter(Boolean);
    if (!ids.length) return toast.info("Nothing to remind — this staff member is fully compliant.");
    try {
      await api.post(`/training/staff/${staffId}/reminder`, { course_ids: ids, message: "Please renew the outstanding training courses." });
      toast.success("Reminder queued");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 z-40" onClick={onClose} data-testid="staff-drawer-backdrop" />
      <aside className="fixed right-0 top-0 h-full w-full sm:max-w-lg bg-white z-50 shadow-xl border-l border-slate-200 flex flex-col" data-testid="staff-profile-drawer">
        <div className="p-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Training Profile</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900" data-testid="staff-profile-name">{data?.staff?.name || "Loading…"}</h2>
            <div className="text-[11px] text-slate-500 mt-0.5 capitalize">{(data?.staff?.role || "").replace(/_/g, " ")} · {data?.staff?.email}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="btn-staff-drawer-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="text-sm text-slate-500">Loading profile…</div>
          ) : !data ? (
            <div className="text-sm text-slate-500">Nothing to show yet.</div>
          ) : (
            <>
              {/* Compliance ring */}
              <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center gap-4">
                <ComplianceRing pct={data.overall_pct} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Overall Compliance</div>
                  <div className="font-heading text-2xl font-semibold text-slate-900 tabular-nums" data-testid="staff-profile-pct">{data.overall_pct}%</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {data.compliant_now} of {data.total_required} required courses met
                  </div>
                </div>
                <div className="ml-auto flex flex-col gap-2">
                  {canManage && <Button size="sm" onClick={() => setAddOpen(true)} data-testid="btn-staff-add-training" className="h-8 bg-sky-700 hover:bg-sky-800 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add Training</Button>}
                  {canManage && <Button size="sm" variant="outline" onClick={sendReminder} data-testid="btn-staff-send-reminder" className="h-8 text-xs"><Send className="w-3.5 h-3.5 mr-1" /> Send Reminder</Button>}
                </div>
              </section>

              <Group title="Expired" icon={ShieldAlert} tone="red" items={data.expired} testid="staff-expired" />
              <Group title="Expiring soon" icon={CalendarClock} tone="amber" items={data.expiring} testid="staff-expiring" />
              <Group title="Missing mandatory" icon={GraduationCap} tone="red" items={data.missing} testid="staff-missing" />
              <Group title="Completed / Compliant" icon={GraduationCap} tone="emerald" items={data.completed} testid="staff-completed" onOpenCert={setCertRow} />
            </>
          )}
        </div>
      </aside>

      <TrainingRecordDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        record={{ staff_user_id: staffId, staff_name: data?.staff?.name }}
        onSaved={() => { setAddOpen(false); load(); onChanged?.(); }}
      />
      {certRow && (
        <div className="fixed inset-0 bg-slate-900/60 z-[60] flex items-center justify-center" onClick={() => setCertRow(null)}>
          <div className="bg-white rounded-lg p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-base font-semibold">{certRow.course_name} — Certificate</h3>
              <button onClick={() => setCertRow(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-500 mb-2">Certificate ID: {certRow.certificate_id || "—"}</div>
            <FileAttachments module="training" recordId={certRow.record_id} />
          </div>
        </div>
      )}
    </>
  );
}

function Group({ title, icon: Icon, tone, items, testid, onOpenCert }) {
  const toneCls = {
    red: "text-red-700",
    amber: "text-amber-700",
    emerald: "text-emerald-700",
    sky: "text-sky-700",
  }[tone] || "text-slate-700";
  if (!items || items.length === 0) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${toneCls}`} />
        <h3 className="font-heading text-sm font-semibold text-slate-900">{title} <span className="text-slate-400 font-normal">({items.length})</span></h3>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((it, i) => (
          <li key={i} className="py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm text-slate-900 truncate">{it.course_name}</div>
              <div className="text-[11px] text-slate-500">{it.category || "—"} · {it.completed_date ? `Completed ${it.completed_date}` : "No completion date"}{it.expiry_date ? ` · Expires ${it.expiry_date}` : ""}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StatusChip status={it.status} />
              {it.record_id && onOpenCert && (
                <button
                  onClick={() => onOpenCert(it)}
                  className="p-1 text-slate-500 hover:text-sky-700"
                  data-testid={`btn-staff-cert-${it.record_id}`}
                  title="Upload / view certificate"
                ><FileText className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ComplianceRing({ pct }) {
  const p = Math.max(0, Math.min(100, pct ?? 0));
  const size = 56, stroke = 6, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const dash = (p / 100) * c;
  const color = p >= 90 ? "#059669" : p >= 70 ? "#d97706" : "#dc2626";
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} stroke="#e2e8f0" strokeWidth={stroke} fill="none" />
      <circle cx={size/2} cy={size/2} r={r} stroke={color} strokeWidth={stroke} strokeLinecap="round" fill="none"
        strokeDasharray={`${dash} ${c - dash}`} transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x="50%" y="52%" dominantBaseline="middle" textAnchor="middle" fontSize="11" fontWeight="600" fill="#0f172a">{Math.round(p)}%</text>
    </svg>
  );
}
